<template>
  <div>
    <NumeraiChart
      class="mt-2 mb-2"
      header="Performance"
      :description="
        'Live scores over past 1 year of resolved rounds' +
          (isResolvedOnlyDisplay
            ? ','
            : ' and score estimates from resolving rounds,') +
          ' indexed by round resolve date.'
      "
      :enable-zoom="true"
      :enable-cumsum="true"
      :loading="isFetchingScoresData"
      :chart-options="{
        chartData: submissionScoreChartData,
        dataLabels: scoreDisplayNames,
        chartColors: [null, '#ff6e40', '#000000', '#ff6e40'],
        dashedLines: [null, null, [2, 2], [2, 2]],
        legend: false,
        verticalGridLines: true,
        height: '400px',
      }"
    >
      <template #menu-items>
        <v-list-item>
          <v-list-item-action>
            <v-switch
              v-model="isResolvedOnlyDisplay"
              color="green"
              dense
            ></v-switch>
          </v-list-item-action>
          <v-list-item-title>Resolved Only</v-list-item-title>
        </v-list-item>

        <v-list-item>
          <v-list-item-action>
            <v-switch v-model="showFullHistory" color="green" dense></v-switch>
          </v-list-item-action>
          <v-list-item-title>Full History</v-list-item-title>
        </v-list-item>
      </template>
    </NumeraiChart>

    <NumeraiChart
      header="Stake"
      :description="
        'At-risk stake value over past 1 year of resolving and resolved rounds,' +
          ' indexed by round close date.'
      "
      class="mb-2"
      :enable-zoom="true"
      :loading="isFetchingScoresData"
      :chart-options="{
        chartData: [stakeGraphData],
        dataLabels: ['NMR'],
        chartColors: [null, '#ff6e40'],
        verticalGridLines: true,
        area: true,
        height: '400px',
      }"
    />
    <div class="numerai-paper pa-6 mb-4">
      <div class="rounds-table">
        <div class="card-title-container">
          <div>
            <h1 class="numerai-section-heading">Resolving Rounds</h1>
            <div class="card-description">
              Rounds that are actively scoring. Score and payout estimates are
              updated daily.
            </div>
          </div>
        </div>
        <div v-if="isFetchingScoresData">
          <NumeraiLoader />
        </div>
        <div v-else>
          <div class="table-container">
            <ProfileScoresTable
              :submission-entries="
                submissionEntries.filter(e => !e.roundResolved)
              "
              :username="username"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="numerai-paper pa-6 mb-2">
      <div class="rounds-table">
        <div class="card-title-container">
          <div>
            <h1 class="numerai-section-heading">Resolved Rounds</h1>
            <div class="card-description">
              Rounds that have been resolved. Scores and payouts are final.
            </div>
          </div>
        </div>
        <div v-if="isFetchingScoresData">
          <NumeraiLoader />
        </div>
        <div v-else>
          <div class="table-container">
            <ProfileScoresTable
              :submission-entries="
                submissionEntries.filter(e => e.roundResolved)
              "
              :username="username"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProfileScoresTable from '@/components/profile/ProfileScoresTable'
import NumeraiLoader from '@/components/shared/NumeraiLoader'
import _ from 'lodash'
import {
  sendLoadTimeToPlausible,
  formatScore,
  formatCurrency,
  formatPercentile,
  formatStakeType,
  getTournamentNumber,
  getTournamentScoresConfig,
  isAppClassic,
} from '@/lib/utils'
import moment from 'moment'
import ls from 'local-storage'
import NumeraiChart from '@/components/charts/NumeraiChart.vue'

export default {
  components: {
    NumeraiChart,
    NumeraiLoader,
    ProfileScoresTable,
  },
  props: {
    // Profile info
    profile: {
      type: Object,
      default: function() {
        return {}
      },
    },
    nmrPrice: {
      type: String,
      default: '',
    },
    username: { type: String, default: null },
    isMobile: { type: Boolean, default: false },
    isDarkMode: { type: Boolean, default: false },
  },
  data() {
    return {
      profileNotFound: false,
      isResolvedOnlyDisplay: true,
      showFullHistory: false,
      scoresData: [],
      userScores: [],
      isFetchingScoresData: false,
      isFetchingUserScores: false,
    }
  },
  computed: {
    roundModelPerformances() {
      if (!this.scoresData) {
        return []
      }

      // create map where round number is the key
      // for each row in scoresData, set score value + percentile for that day..
      // if first score for that day, set date, payout, etc.
      // sort rows by day desc
      let res = new Map()

      for (const data of this.scoresData) {
        if (!res.has(data.roundNumber)) {
          res.set(data.roundNumber, {
            roundNumber: data.roundNumber,
            roundResolveTime: data.roundResolveTime,
            roundPayoutFactor: data.roundPayoutFactor,
            roundScoreTime: data.roundScoreTime,
            roundResolved: data.roundResolved,
            roundCloseStakingTime: data.roundCloseStakingTime,
            churnThreshold: data.churnThreshold,
            prevWeekChurnMax: data.prevWeekChurnMax,
            date: data.roundResolveTime,
            atRisk: data.atRisk,
            corrMultiplier: data.corrMultiplier,
            mmcMultiplier: data.mmcMultiplier,
            tcMultiplier: data.tcMultiplier,
          })
        }

        // payout data is in submissionScores, so add it to parent
        if (data.submissionScores) {
          for (const scoreData of data.submissionScores) {
            if (!res.get(data.roundNumber)['payoutPending']) {
              if (!_.isEmpty(scoreData.payoutSettled)) {
                res.get(data.roundNumber)['payoutPending'] =
                  scoreData.payoutSettled
              }
              if (!_.isEmpty(scoreData.payoutPending)) {
                res.get(data.roundNumber)['payoutPending'] =
                  scoreData.payoutPending
              }
            }
            let key = scoreData.displayName
            if (scoreData.displayName === 'v2_corr20') {
              key = 'corr20V2'
            }
            if (scoreData.displayName === 'fnc_v4') {
              key = 'fncV4'
            }
            res.get(data.roundNumber)[key] = scoreData.value
            res.get(data.roundNumber)[key + 'Percentile'] = scoreData.percentile
          }
        }
      }

      return Array.from(res.values())
    },
    tournamentScoresConfig() {
      return getTournamentScoresConfig(
        getTournamentNumber(),
        'showProfileChart',
      )
    },
    scoreNames() {
      return Object.keys(this.tournamentScoresConfig)
    },
    submissionScoreChartData() {
      if (this.performances.length === 0) {
        return
      }

      let points = this.performances.toSorted(
        (a, b) => a.roundNumber - b.roundNumber,
      )

      if (!points) {
        return
      }
      let res = []
      this.scoreNames.forEach(scoreName => {
        res.push(
          points.map(p => ({
            x: p.date,
            y: p[scoreName] || 0,
            percentile: p[scoreName + 'Percentile'],
            round: p.roundNumber,
          })),
        )
      })
      return res
    },
    stakeGraphData() {
      return this.roundModelPerformances
        .toSorted((a, b) => a.roundNumber - b.roundNumber)
        .map(p => ({
          x: p.roundCloseStakingTime,
          y: p.atRisk,
          round: p.roundNumber,
        }))
    },
    scoreDisplayNames() {
      let res = []
      this.scoreNames.forEach(scoreName => {
        res.push(this.tournamentScoresConfig[scoreName]['chartLabel'])
      })
      return res
    },
    performances() {
      let rmp = this.roundModelPerformances.filter(p =>
        this.isResolvedOnlyDisplay ? p.roundResolved : true,
      )
      switch (getTournamentNumber()) {
        case 8:
          rmp = rmp.map(p => {
            let {
              canon_corr,
              canon_corrPercentile,
              canon_mmc,
              canon_mmcPercentile,
              roundNumber,
              roundScoreTime,
              roundCloseStakingTime,
              date,
            } = p
            return {
              corr20V2: canon_corr,
              corr20V2Percentile: canon_corrPercentile,
              mmc: canon_mmc,
              mmcPercentile: canon_mmcPercentile,
              roundNumber,
              roundScoreTime,
              roundCloseStakingTime,
              date,
            }
          })
          break

        case 11:
          rmp = rmp.map(p => {
            let {
              canon_corr,
              canon_corrPercentile,
              canon_mmc,
              canon_mmcPercentile,
              roundNumber,
              roundScoreTime,
              roundCloseStakingTime,
              date,
            } = p
            return {
              fncV4: canon_corr,
              fncV4Percentile: canon_corrPercentile,
              mmc: canon_mmc,
              mmcPercentile: canon_mmcPercentile,
              roundNumber,
              roundScoreTime,
              roundCloseStakingTime,
              date,
            }
          })
          break

        case 12:
          rmp = rmp.map(p => {
            let {
              canon_corr,
              canon_corrPercentile,
              canon_mmc,
              canon_mmcPercentile,
              roundNumber,
              roundScoreTime,
              roundCloseStakingTime,
              date,
            } = p
            return {
              corr: canon_corr,
              corrPercentile: canon_corrPercentile,
              mmc: canon_mmc,
              mmcPercentile: canon_mmcPercentile,
              roundNumber,
              roundScoreTime,
              roundCloseStakingTime,
              date,
            }
          })
          break
      }
      return rmp
    },
    submissionEntries() {
      // sort by round number
      return this.roundModelPerformances.toSorted(
        (a, b) => b.roundNumber - a.roundNumber,
      )
    },
    oneDayReturns() {
      return this.profile.latestReturns.oneDay || 0
    },
    threeMonthsReturns() {
      return this.profile.latestReturns.threeMonths || 0
    },
    oneYearReturns() {
      return this.profile.latestReturns.oneYear || 0
    },
  },
  watch: {
    isResolvedOnlyDisplay(value) {
      ls.set('isResolvedOnlyDisplay', value)
    },
    showFullHistory(value) {
      this.fetchRoundModelPerformances(this.profile.id)
      ls.set('showFullHistory', value)
    },
  },
  mounted() {
    sendLoadTimeToPlausible('profile')
  },
  async created() {
    const local_isResolvedOnlyDisplay = ls.get('isResolvedOnlyDisplay')
    const local_showFullHistory = ls.get('showFullHistory')
    if (local_isResolvedOnlyDisplay !== null) {
      this.isResolvedOnlyDisplay = local_isResolvedOnlyDisplay
    }
    if (local_showFullHistory !== null) {
      this.showFullHistory = local_showFullHistory
    }
    this.fetchRoundModelPerformances(this.profile.id)
  },
  methods: {
    isAppClassic,
    formatStakeType,
    formatScore,
    formatCurrency,
    formatPercentile,
    formatDate(x) {
      return moment(x)
        .utc()
        .format('MMM DD YYYY')
    },
    fromNow(date) {
      return moment(new Date(date))
        .endOf('day')
        .fromNow()
    },
    daysSinceWoke() {
      const days = moment().diff(this.profile.startDate, 'days')
      return days
    },
    async fetchRoundModelPerformances(modelId) {
      this.isFetchingScoresData = true
      let rmpQueryArgs = {
        modelId: modelId,
        tournament: getTournamentNumber(),
        resolvedWithinLastNDays: this.showFullHistory
          ? this.daysSinceWoke()
          : 365,
        distinctOnRound: true,
      }
      this.scoresData = await this.$client.v2RoundModelPerformances(
        rmpQueryArgs,
      )
      this.isFetchingScoresData = false
    },
    async fetchUserScores(modelId) {
      this.isFetchingUserScores = true
      this.userScores = await this.$client.userScores(modelId)
      this.isFetchingUserScores = false
    },
  },
}
</script>
<style scoped lang="scss">
.card-title-container {
  display: flex;
  flex-direction: column;
}

.card-description {
  display: flex;
  justify-content: space-between;
  color: var(--color-secondary);
  margin-bottom: 1em;
}

.card-description a {
  color: var(--color-secondary);
  text-align: right;
}

.two-column {
  margin-bottom: 1.8em;
  flex-direction: row;
  justify-content: space-between;
}

.two-column .card-description {
  margin-bottom: 0;
}

.table-container {
  overflow: auto;
}
</style>
